import axios from "axios";
import { Dispatch } from "react";
import { fetchArticlesByBlogHandle, fetchArticleByHandle } from "../../clients/shopify/shopifyClient";
import { transformShopifyArticlesToRecipes, transformShopifyArticleToRecipe } from "../../clients/shopify/shopifyBlogTransformer";
import { getCachedData, setCachedData } from "../../utils/cacheUtils";
// Recipe type is used in the function signatures

export const getRecipesFromDatabase = () => (dispatch: Dispatch<any>) => {
    return axios
        .get(process.env.REACT_APP_BACKEND_URL + `/recipes`)
        .then((res) => {
            dispatch(recipesData(res.data));
        })
        .catch((err) => {
            console.log(err);
        });
};

export const getRecipeItemsFromDatabase = () => (dispatch: Dispatch<any>, idList: number[]) => {
    const idListQ = JSON.stringify(idList);
    if (idList && idList.length > 0)
    {
        return axios
            .get(process.env.REACT_APP_BACKEND_URL + `/search/idlist?q=${idListQ}`)
            .then((res) => {
                dispatch(recipesItemData(res.data));
            })
            .catch((err) => {
                console.log(err);
            });
    }
    return [];
}


const recipesItemData = (recipeItems: ItemData[]) => {
    return {
        type: "SELECT_RECIPE",
        itemsData: recipeItems,
    };
};

const recipesData = (data: RecipeData[]) => {
    return {
        type: "GET_RECIPES",
        recipesData: data,
    };
};

// Action types for Shopify recipes
export const RECIPE_ACTION_TYPES = {
    FETCH_RECIPES: 'FETCH_RECIPES',
    FETCH_RECIPE: 'FETCH_RECIPE',
    SEARCH_RECIPES: 'SEARCH_RECIPES',
    SET_LOADING_STATE: 'RECIPE_SET_LOADING_STATE',
    SET_ERROR_STATE: 'RECIPE_SET_ERROR_STATE'
};

/**
 * Fetch recipes from Shopify blog
 */
export const fetchRecipesFromShopify = (blogHandle: string = 'recipes', cursor?: string) => async (dispatch: any) => {
    try {
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: true });
        
        // Check if this is a forced refresh request (from mobile navigation)
        const isRefreshRequest = cursor && cursor.startsWith('refresh_');
        
        if (!isRefreshRequest) {
            // Create a cache key based on the cursor
            const cacheKey = `recipes_${blogHandle}_${cursor || 'initial'}`;
            
            // Try to get data from cache first (only if not a refresh request)
            const cachedData = getCachedData(cacheKey);
            if (cachedData) {
                console.log('Using cached recipes data');
                
                dispatch({
                    type: RECIPE_ACTION_TYPES.FETCH_RECIPES,
                    payload: cachedData.recipes,
                    paginationInfo: cachedData.paginationInfo
                });
                
                dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
                return cachedData.recipes;
            }
        } else {
            console.log('Bypassing cache for recipes - refresh requested');
            // For refresh requests, we'll proceed directly to fetching from API
            // Reset cursor to null to fetch from the beginning
            cursor = null;
        }
        
        // If not in cache, fetch from API
        const response = await fetchArticlesByBlogHandle(blogHandle, isRefreshRequest ? null : cursor);
        const transformedRecipes = transformShopifyArticlesToRecipes(response.articles);
        
        // Prepare pagination info
        const paginationInfo = {
            hasNextPage: response.pageInfo.hasNextPage,
            endCursor: response.pageInfo.endCursor,
            collectionHandle: blogHandle,
            searchTerm: null
        };
        
        // Cache the results (but not for refresh requests)
        if (!isRefreshRequest) {
            const cacheKey = `recipes_${blogHandle}_${cursor || 'initial'}`;
            setCachedData(cacheKey, {
                recipes: transformedRecipes,
                paginationInfo
            });
        }
        
        dispatch({
            type: RECIPE_ACTION_TYPES.FETCH_RECIPES,
            payload: transformedRecipes,
            paginationInfo
        });
        
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
        return transformedRecipes;
    } catch (error) {
        console.error('Error fetching recipes:', error);
        dispatch({ type: RECIPE_ACTION_TYPES.SET_ERROR_STATE, error: 'Failed to fetch recipes' });
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
        return [];
    }
};

/**
 * Search recipes by term
 */
export const searchRecipesByTerm = (searchTerm: string, blogHandle: string = 'recipes') => async (dispatch: any) => {
    try {
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: true });
        
        // Create a cache key for this search term
        const cacheKey = `search_${blogHandle}_${searchTerm}`;
        
        // Try to get data from cache first
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
            console.log(`Using cached search results for: ${searchTerm}`);
            
            dispatch({
                type: RECIPE_ACTION_TYPES.SEARCH_RECIPES,
                payload: cachedData.recipes,
                paginationInfo: cachedData.paginationInfo
            });
            
            dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
            return cachedData.recipes;
        }
        
        // If not in cache, fetch from API
        // First fetch all articles (with default limit of 20)
        const response = await fetchArticlesByBlogHandle(blogHandle);
        const allRecipes = transformShopifyArticlesToRecipes(response.articles);
        
        // Then filter the articles by search term
        const searchTermLower = searchTerm.toLowerCase();
        const filteredRecipes = allRecipes.filter(recipe => {
            return (
                recipe.title.toLowerCase().includes(searchTermLower) ||
                (recipe.excerpt && recipe.excerpt.toLowerCase().includes(searchTermLower)) ||
                (recipe.contentHtml && recipe.contentHtml.toLowerCase().includes(searchTermLower)) ||
                (recipe.tags && recipe.tags.some(tag => tag.toLowerCase().includes(searchTermLower)))
            );
        });
        
        // Prepare pagination info
        const paginationInfo = {
            hasNextPage: false, // Search results don't have pagination
            endCursor: null,
            collectionHandle: blogHandle,
            searchTerm
        };
        
        // Cache the results
        setCachedData(cacheKey, {
            recipes: filteredRecipes,
            paginationInfo
        });
        
        dispatch({
            type: RECIPE_ACTION_TYPES.SEARCH_RECIPES,
            payload: filteredRecipes,
            paginationInfo
        });
        
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
        return filteredRecipes;
    } catch (error) {
        console.error('Error searching recipes:', error);
        dispatch({ type: RECIPE_ACTION_TYPES.SET_ERROR_STATE, error: `Failed to search recipes: ${searchTerm}` });
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
        return [];
    }
};

/**
 * Fetch a single recipe by its handle
 */
export const fetchRecipeByHandle = (blogHandle: string = 'recipes', recipeHandle: string) => async (dispatch: any) => {
    try {
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: true });
        
        // Create a cache key for this specific recipe
        const cacheKey = `recipe_${blogHandle}_${recipeHandle}`;
        
        // Try to get data from cache first
        const cachedRecipe = getCachedData(cacheKey);
        if (cachedRecipe) {
            console.log(`Using cached recipe data for: ${recipeHandle}`);
            
            dispatch({
                type: RECIPE_ACTION_TYPES.FETCH_RECIPE,
                payload: cachedRecipe
            });
            
            dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
            return cachedRecipe;
        }
        
        // If not in cache, fetch from API
        const article = await fetchArticleByHandle(blogHandle, recipeHandle);
        const transformedRecipe = article ? transformShopifyArticleToRecipe(article) : null;
        
        if (transformedRecipe) {
            // Cache the recipe
            setCachedData(cacheKey, transformedRecipe);
            
            dispatch({
                type: RECIPE_ACTION_TYPES.FETCH_RECIPE,
                payload: transformedRecipe
            });
        }
        
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
        return transformedRecipe;
    } catch (error) {
        console.error('Error fetching recipe by handle:', error);
        dispatch({ type: RECIPE_ACTION_TYPES.SET_ERROR_STATE, error: `Failed to fetch recipe: ${recipeHandle}` });
        dispatch({ type: RECIPE_ACTION_TYPES.SET_LOADING_STATE, isLoading: false });
        return null;
    }
};
